import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Search } from 'lucide-react'

import { useAlgoliaClients } from '@/shared/hooks/useAlgoliaClients'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { ScrollArea } from '@/shared/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Client } from '@monorepo/functions/src/types/client'

interface ClientSearchModalProps {
    isOpen: boolean
    onClose: () => void
    onClientSelect: (clientId: string, petIndex?: number) => void
    redirectPath: string
    requirePetSelection?: boolean
}

export function ClientSearchModal({
    isOpen,
    onClose,
    onClientSelect,
    redirectPath,
    requirePetSelection = false,
}: ClientSearchModalProps) {
    const { t } = useTranslation()
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedClient, setSelectedClient] = useState<Client | null>(null)
    const [selectedPetIndex, setSelectedPetIndex] = useState<number | undefined>(0)

    const { results: filteredClients } = useAlgoliaClients({
        searchQuery: searchTerm,
    })

    const handleClientSelect = (client: Client) => {
        setSelectedClient(client)
    }

    const handleNewClient = () => {
        window.location.href = window.location.origin + redirectPath
        onClose()
    }

    const handleContinue = () => {
        if (selectedClient) {
            let petIndex: number | undefined = undefined
            if (requirePetSelection && selectedPetIndex) {
                petIndex = selectedPetIndex
            }
            onClientSelect(selectedClient.id, petIndex)
            onClose()
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='sm:max-w-[425px]'>
                <DialogHeader>
                    <DialogTitle>{t('clientSearch.title', 'Buscar Cliente')}</DialogTitle>
                </DialogHeader>
                <div className='grid gap-4 py-4'>
                    <div className='relative'>
                        <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
                        <Input
                            placeholder={t(
                                'clientSearch.placeholder',
                                'Buscar por nombre, teléfono, email o nombre de la mascota',
                            )}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className='pl-8'
                        />
                    </div>
                    <ScrollArea className='h-[300px] pr-4'>
                        <div className='space-y-2'>
                            {filteredClients.map(client => (
                                <div
                                    key={client.id}
                                    className={`group flex cursor-pointer flex-col rounded-lg border p-3 transition-all hover:border-primary hover:shadow-sm ${
                                        selectedClient?.id === client.id
                                            ? 'border-primary bg-primary/5 shadow-sm'
                                            : 'border-border'
                                    }`}
                                    onClick={() => handleClientSelect(client)}
                                >
                                    <div className='flex items-center justify-between'>
                                        <div className='font-medium group-hover:text-primary'>
                                            {client.name} {client.lastName}
                                        </div>
                                        <div className='text-xs text-muted-foreground'>
                                            {client.pets.length} {client.pets.length === 1 ? 'mascota' : 'mascotas'}
                                        </div>
                                    </div>
                                    <div className='mt-1 text-xs text-muted-foreground'>
                                        {client.phone} • {client.email}
                                    </div>
                                    {client.pets.length > 0 && (
                                        <div className='mt-1.5 flex flex-wrap gap-1'>
                                            {client.pets.map(pet => (
                                                <span
                                                    key={pet.id}
                                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
                                                        selectedClient?.id === client.id
                                                            ? 'bg-primary/10 text-primary'
                                                            : 'bg-muted text-muted-foreground'
                                                    }`}
                                                >
                                                    {pet.name}
                                                    {pet.breed && ` • ${pet.breed}`}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {filteredClients.length === 0 && (
                                <div className='py-4 text-center text-muted-foreground'>No se encontraron clientes</div>
                            )}
                        </div>
                    </ScrollArea>

                    {requirePetSelection && selectedClient && (
                        <div className='space-y-2'>
                            <label className='text-sm font-medium'>Seleccionar Mascota</label>
                            <Select
                                value={selectedPetIndex?.toString()}
                                onValueChange={value => setSelectedPetIndex(Number(value))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder='Selecciona una mascota' />
                                </SelectTrigger>
                                <SelectContent>
                                    {selectedClient.pets.map((pet, index) => (
                                        <SelectItem key={index} value={index.toString()}>
                                            {pet.name} {pet.breed ? `(${pet.breed})` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className='flex justify-between gap-2'>
                        <Button variant='outline' onClick={handleNewClient}>
                            {t('clientSearch.newClient', 'Cliente Nuevo')}
                        </Button>
                        <Button onClick={handleContinue} disabled={!selectedClient}>
                            {t('clientSearch.continue', 'Continuar')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
